import type { Practitioner, Reference, Task } from '@medplum/fhirtypes'
import type { BundleMutationBuilder } from '#src/bundle-builder.ts'
import { type IDTRole, idtRoles } from '#src/models/care-teams/constants.ts'
import type { IDT } from '#src/models/care-teams/idt.ts'
import { SDRSignatureTask } from '#src/models/tasks/signature/sdrSignatureTask.ts'
import { queryTypesToString } from '#src/search.ts'

// IDT roles with no live signature task, mirroring the byRoles
// conditional create: deleted, canceled, and duplicate tasks don't count.
export function uncoveredSDRRoles(signatures: SDRSignatureTask[]): IDTRole[] {
  const covered = new Set(
    signatures.flatMap((signature) =>
      !signature.isDeleted &&
      signature.state !== 'canceled' &&
      signature.state !== 'duplicate'
        ? [signature.role]
        : []
    )
  )
  return idtRoles.filter((role) => !covered.has(role))
}

// One signature per IDT role, not per practitioner, same as
// createCarePlanSignatures. A practitioner seated in two roles gets one task
// per role; an unstaffed role yields a task with no owner. ifNoneExist makes
// re-requesting fill only the gaps.
export function createSDRSignatures(
  transaction: BundleMutationBuilder,
  {
    sdr,
    idt,
    requester,
  }: {
    sdr: Reference<Task>
    idt: IDT
    requester: Reference<Practitioner>
  }
): void {
  idtRoles.forEach((role) => {
    transaction.create(
      new SDRSignatureTask({
        sdr,
        requester,
        role,
        signer: idt.getMemberByRole(role)?.practitioner,
      }),
      {
        request: {
          ifNoneExist: queryTypesToString(
            SDRSignatureTask.byRoles({ sdr, role })
          ),
        },
      }
    )
  })
}
